'use client'

import { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'

type ReferrerContextType = {
  previousUrl: string | null
  currentUrl: string | null
  originalAreasUrl: string | null
  setPreviousUrl: (url: string) => void
  isFromWikiPage: boolean
  isFromProductGroup: boolean
  lastSegmentFromAreas: string | null
}

const ReferrerContext = createContext<ReferrerContextType>({
  previousUrl: null,
  currentUrl: null,
  originalAreasUrl: null,
  setPreviousUrl: () => {},
  isFromWikiPage: false,
  isFromProductGroup: false,
  lastSegmentFromAreas: null
})

export const ReferrerContextProvider = ({ children }: { children: ReactNode }) => {
  const [previousUrl, setPreviousUrl] = useState<string | null>(null)
  const [currentUrl, setCurrentUrl] = useState<string | null>(null)
  // Store the original areas URL to maintain reference across multi-step navigation
  const [originalAreasUrl, setOriginalAreasUrl] = useState<string | null>(null)
  const pathname = usePathname()
  
  // Check if the previous URL was from areas (wiki) page
  const isFromWikiPage = Boolean(previousUrl?.includes('/areas/'))
  
  // Check if from product group page (has /products/ but not /products/group/product)
  const isFromProductGroup = useMemo(() => {
    if (!previousUrl) return false;
    
    const segments = previousUrl.split('/').filter(Boolean);
    const productsIndex = segments.findIndex(segment => segment === 'products');
    
    // Is a product page but not a product detail page (should have only one path segment after 'products')
    return productsIndex > 0 && segments.length === productsIndex + 2;
  }, [previousUrl]);
  
  // Extract the last segment from areas URL if coming from areas page
  const lastSegmentFromAreas = useMemo(() => {
    // If we have stored an original areas URL, use it
    if (originalAreasUrl) {
      const segments = originalAreasUrl.split('/')
      return segments[segments.length - 1] || null
    }
    // Otherwise check if coming directly from areas
    else if (isFromWikiPage && previousUrl) {
      const segments = previousUrl.split('/')
      return segments[segments.length - 1] || null
    }
    return null
  }, [isFromWikiPage, previousUrl, originalAreasUrl])

  // Update URLs when pathname changes
  useEffect(() => {
    if (pathname) {
      if (currentUrl && currentUrl !== pathname) {
        // If we're coming from an areas page, store it as the original areas URL
        if (currentUrl.includes('/areas/')) {
          setOriginalAreasUrl(currentUrl)
          sessionStorage.setItem('originalAreasUrl', currentUrl)
        }
        
        // Keep the original areas URL when moving through products/solutions flow
        if (pathname.includes('/technology/') || (pathname.includes('/portfolio/') && pathname.split('/').filter(Boolean).length >= 3)) {
          // Don't reset originalAreasUrl - keep it for the entire product navigation flow
        }
        // If we're navigating away from the product or solution flow, clear the original areas URL
        else {
          setOriginalAreasUrl(null)
          sessionStorage.removeItem('originalAreasUrl')
        }
        
        // Store the current URL before updating it
        sessionStorage.setItem('previousUrl', currentUrl)
        setPreviousUrl(currentUrl)
      }
      setCurrentUrl(pathname)
    }
  }, [pathname, currentUrl])

  // Initialize from sessionStorage on client-side only on first load
  useEffect(() => {
    // Only run once on component mount
    if (typeof window !== 'undefined') {
      const storedPreviousUrl = sessionStorage.getItem('previousUrl')
      const storedOriginalAreasUrl = sessionStorage.getItem('originalAreasUrl')
      
      if (storedPreviousUrl) {
        setPreviousUrl(storedPreviousUrl)
      }
      
      if (storedOriginalAreasUrl) {
        // Only restore originalAreasUrl if we're currently on a product/solution detail page.
        // Otherwise, the user has navigated away (e.g. to products-solutions overview)
        // and the stale areas URL should not be restored.
        if (pathname?.includes('/technology/') || (pathname?.includes('/portfolio/') && pathname.split('/').filter(Boolean).length >= 3)) {
          setOriginalAreasUrl(storedOriginalAreasUrl)
        } else {
          sessionStorage.removeItem('originalAreasUrl')
        }
      }
      
      // Set current URL on initial load
      if (pathname) {
        setCurrentUrl(pathname)
      }
      
      // Log for debugging
      console.log('Initialized referrer context:', {
        previousUrl: storedPreviousUrl,
        currentUrl: pathname,
        originalAreasUrl: storedOriginalAreasUrl
      });
    }
  }, []) // Empty dependency array ensures this runs only once

  // Update sessionStorage before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentUrl) {
        sessionStorage.setItem('previousUrl', currentUrl)
      }
      if (originalAreasUrl) {
        sessionStorage.setItem('originalAreasUrl', originalAreasUrl)
      }
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [currentUrl, originalAreasUrl])

  return (
    <ReferrerContext.Provider 
      value={{ 
        previousUrl, 
        currentUrl,
        originalAreasUrl,
        setPreviousUrl,
        isFromWikiPage,
        isFromProductGroup,
        lastSegmentFromAreas
      }}
    >
      {children}
    </ReferrerContext.Provider>
  )
}

export const useReferrerContext = () => useContext(ReferrerContext) 